import { Box, Button, Chip, Typography } from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import React, { useState, useEffect } from 'react';
import { CameraIcon } from 'components/icons';
import {
	setTicketCaptureMode,
	setTicketCaptureCloser,
} from 'components/onboarding/tour/ticketingCaptureState';
import { useCANparkHome } from '../../hooks/useCANparkHome';
import { useNotificationProvider } from 'hooks';
import ActiveVehicleDrawer from '../activeVehicleDrawer/ActiveVehicleDrawer';
import ScanningComponent from './ScanningComponent';
import ContextBar from './ContextBar';
import ManualEntrySheet from './ManualEntrySheet';
import ScanPreview from './ScanPreview';
import { useCANparkRoleContext } from '../../context/CANparkRoleProvider';

const CANparkHomeTab: React.FC = () => {
	const {
		activeParkingLocation,
		setActiveParkingLocation,
		selectedState,
		setSelectedState,
		licensePlateNumber,
		setLicensePlateNumber,
		selectedCustomer,
		setSelectedCustomer,
		customers,
		loadingCustomers,
		isCameraOn,
		setIsCameraOn,
		capturedImage,
		setCapturedImage,
		handleViewLicenseHistoryClick,
		handleAutoAdvanceFromScan,
	} = useCANparkHome();

	const { notifyError } = useNotificationProvider();
	const { isLimited } = useCANparkRoleContext();
	const [manualOpen, setManualOpen] = useState(false);

	const canSubmitManual =
		!!licensePlateNumber && !!selectedState && !!activeParkingLocation;

	const handleManualSubmit = () => {
		if (!canSubmitManual) return;
		handleViewLicenseHistoryClick();
		setManualOpen(false);
	};

	const handleScanPress = () => {
		if (!activeParkingLocation) {
			notifyError('Pick a parking lot first');
			return;
		}
		// Drop any previous preview before starting a fresh scan so a stale
		// image can't flash in if the user backs out before a clean OCR.
		setCapturedImage(null);
		setIsCameraOn(true);
	};

	// Preview mode is reached when a scan finished and the lot keeps the
	// preview step on: camera is off and an image is captured. When the
	// preview is disabled the drawer takes over instead and the captured
	// image is cleared from the home hook.
	const showScanPreview =
		!isCameraOn &&
		!!capturedImage &&
		activeParkingLocation?.showScanPreview !== false;

	// Tell the ticketing tour which plate-capture phase we're in (camera, manual
	// sheet, or scan preview) so it steps aside with the right message/placement
	// and doesn't block those controls. Reset on unmount so it can't stick.
	useEffect(() => {
		setTicketCaptureMode(
			isCameraOn
				? 'camera'
				: manualOpen
					? 'manual'
					: showScanPreview
						? 'preview'
						: 'none',
		);
	}, [isCameraOn, manualOpen, showScanPreview]);
	useEffect(() => () => setTicketCaptureMode('none'), []);

	// Let the tour dismiss the active capture UI (e.g. Back on the capture step):
	// clearing these flips captureMode to 'none' and the tour drops back to scan.
	useEffect(() => {
		setTicketCaptureCloser(() => {
			setIsCameraOn(false);
			setManualOpen(false);
			setCapturedImage(null);
		});
		return () => setTicketCaptureCloser(null);
	}, [setIsCameraOn, setCapturedImage]);

	const handlePreviewConfirm = () => {
		// handleViewLicenseHistoryClick handles the full validation +
		// notifyError path and clears the captured image on success.
		handleViewLicenseHistoryClick();
	};

	const handlePreviewRetry = () => {
		setCapturedImage(null);
		setIsCameraOn(true);
	};

	const handleManualPress = () => {
		if (!activeParkingLocation) {
			notifyError('Pick a parking lot first');
			return;
		}
		setManualOpen(true);
	};

	// Camera mode takes the whole tab — context bar and CTAs collapse so
	// the operator's full attention is on the viewfinder.
	if (isCameraOn) {
		return (
			<Box
				sx={{
					width: '100%',
					maxWidth: 500,
					mx: 'auto',
					p: 2,
					flex: 1,
					display: 'flex',
					flexDirection: 'column',
					gap: 1,
				}}
			>
				{activeParkingLocation && (
					<Box
						sx={{
							px: 1.5,
							py: 0.75,
							borderRadius: 2,
							bgcolor: 'action.hover',
						}}
					>
						<Typography
							variant="caption"
							color="text.secondary"
							component="div"
						>
							Scanning at
						</Typography>
						<Typography
							variant="body2"
							fontWeight={500}
							noWrap
						>
							{activeParkingLocation.name}
						</Typography>
					</Box>
				)}
				<ScanningComponent
					setLicensePlateNumber={setLicensePlateNumber}
					setSelectedState={setSelectedState}
					setIsCameraOn={setIsCameraOn}
					capturedImage={capturedImage}
					setCapturedImage={setCapturedImage}
					onCleanScan={
						activeParkingLocation?.showScanPreview === false
							? handleAutoAdvanceFromScan
							: undefined
					}
				/>
				<ActiveVehicleDrawer />
			</Box>
		);
	}

	// Post-scan review mode — only when the lot doesn't auto-advance.
	if (showScanPreview && capturedImage) {
		return (
			<Box
				sx={{
					flex: 1,
					width: '100%',
					maxWidth: 500,
					mx: 'auto',
					display: 'flex',
					flexDirection: 'column',
					minHeight: 0,
				}}
			>
				<Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
					<ContextBar
						customers={customers}
						loadingCustomers={loadingCustomers}
						selectedCustomer={selectedCustomer}
						setSelectedCustomer={setSelectedCustomer}
						activeParkingLocation={activeParkingLocation}
						setActiveParkingLocation={setActiveParkingLocation}
					/>
				</Box>
				<ScanPreview
					capturedImage={capturedImage}
					licensePlateNumber={licensePlateNumber}
					setLicensePlateNumber={setLicensePlateNumber}
					selectedState={selectedState}
					setSelectedState={setSelectedState}
					onConfirm={handlePreviewConfirm}
					onRetry={handlePreviewRetry}
					canConfirm={
						!!licensePlateNumber &&
						!!selectedState &&
						!!activeParkingLocation
					}
					helperText={
						!licensePlateNumber
							? 'Enter the plate to continue'
							: undefined
					}
				/>
				<ActiveVehicleDrawer />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				width: '100%',
				maxWidth: 500,
				mx: 'auto',
				display: 'flex',
				flexDirection: 'column',
				minHeight: 0,
			}}
		>
			{/* Top: context (customer + lot) */}
			<Box sx={{ p: 2, pb: 1, flexShrink: 0 }}>
				<ContextBar
					customers={customers}
					loadingCustomers={loadingCustomers}
					selectedCustomer={selectedCustomer}
					setSelectedCustomer={setSelectedCustomer}
					activeParkingLocation={activeParkingLocation}
					setActiveParkingLocation={setActiveParkingLocation}
				/>
			</Box>

			{/* Spacer pushes the action area into the thumb zone */}
			<Box sx={{ flexGrow: 1, minHeight: 0 }} />

			{/* Bottom action area — the operator's thumb lives here */}
			<Box
				data-tour="ticket-scan-btn"
				sx={{
					px: 2,
					pt: 1,
					pb: 'calc(16px + env(safe-area-inset-bottom))',
					display: 'flex',
					flexDirection: 'column',
					gap: 1,
					flexShrink: 0,
				}}
			>
				{!isLimited && (
					<Box display="flex" justifyContent="flex-end">
						<Chip
							label="Type plate instead"
							icon={<KeyboardIcon />}
							onClick={handleManualPress}
							clickable
							variant="outlined"
							color="primary"
							sx={{
								fontWeight: 500,
								px: 0.5,
								borderWidth: 1.5,
								'& .MuiChip-icon': { ml: 1 },
							}}
						/>
					</Box>
				)}
				<Button
					variant="contained"
					size="large"
					fullWidth
					startIcon={isLimited ? <KeyboardIcon /> : <CameraIcon />}
					onClick={isLimited ? handleManualPress : handleScanPress}
					sx={{
						py: 2,
						borderRadius: 3,
						fontSize: '1rem',
						fontWeight: 500,
						textTransform: 'none',
						boxShadow: 3,
					}}
				>
					{isLimited ? 'Enter License Plate' : 'Scan License Plate'}
				</Button>
			</Box>

			<ManualEntrySheet
				open={manualOpen}
				onClose={() => setManualOpen(false)}
				licensePlateNumber={licensePlateNumber}
				setLicensePlateNumber={setLicensePlateNumber}
				selectedState={selectedState}
				setSelectedState={setSelectedState}
				onSubmit={handleManualSubmit}
				canSubmit={canSubmitManual}
				helperText={
					!activeParkingLocation
						? 'Pick a parking lot first'
						: undefined
				}
			/>

			<ActiveVehicleDrawer />
		</Box>
	);
};

export default CANparkHomeTab;
